import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { GanttTask } from '../types/gantt';
import type { Project } from '../data/projects';
import type { UserResponse } from '../services/users';
import * as tasksAPI from '../services/tasks';
import type {
	TaskResponse,
	TaskAttachment,
	TaskIssue,
	ConflictInfo,
	RescheduleTaskChange
} from '../services/tasks';
import { requestProjectReplan, applyProjectReplan, type ProjectReplanResponse } from '../services/projects';
import { useAuth } from '../services/auth';
import { getStoredCompanyScopeMode } from '../services/companies';
import { useI18n } from '../i18n';
import TaskLegalBadge from './TaskLegalBadge';
import { getTaskLegalMeta } from '../utils/taskLegalStatus';

type TaskFormState = {
	title: string;
	description: string;
	status: GanttTask['status'];
	progress: number;
	startDate: string;
	endDate: string;
	selectedGardeners: string[];
	notes: string;
};

function taskToFormState(task: GanttTask): TaskFormState {
	return {
		title: task.name,
		description: task.description || '',
		status: task.status || 'not_started',
		progress: task.progress ?? 0,
		startDate: task.startDate.split('T')[0],
		endDate: task.endDate.split('T')[0],
		selectedGardeners: task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []),
		notes: task.notes || ''
	};
}

function taskResponseToFormState(t: TaskResponse): TaskFormState {
	return {
		title: t.title,
		description: t.description || '',
		status: t.status || 'not_started',
		progress: t.progress ?? 0,
		startDate: (t.start_date || '').split('T')[0],
		endDate: (t.end_date || '').split('T')[0],
		selectedGardeners: t.gardener_ids.map(String),
		notes: t.notes || ''
	};
}

function taskPropSyncSignature(task: GanttTask): string {
	return [
		task.id,
		task.startDate,
		task.endDate,
		task.progress,
		task.status,
		task.name,
		task.description ?? '',
		task.notes ?? '',
		task.legalSeasonalBanCandidate ? '1' : '0',
		task.legalSeasonalBanReason ?? '',
		(task.gardenerIds || []).slice().sort().join(',')
	].join('|');
}

type TaskDetailModalProps = {
	task: GanttTask | null;
	project: Project | null;
	companyCountryCode?: string | null;
	gardeners: UserResponse[];
	onClose: () => void;
	/** Refresh list / parent state after a mutation (may be async). */
	onUpdate: () => void | Promise<void>;
	/** Optional optimistic patch for parent task lists (e.g. Tasks table) */
	onTaskPatched?: (taskId: number, patch: Partial<TaskResponse>) => void;
};

export default function TaskDetailModal({
	task,
	project,
	companyCountryCode,
	gardeners,
	onClose,
	onUpdate,
	onTaskPatched
}: TaskDetailModalProps) {
	const [isEditing, setIsEditing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [taskData, setTaskData] = useState<TaskFormState | null>(null);
	const [newNote, setNewNote] = useState('');
	const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadDescription, setUploadDescription] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);
	const cameraInputRef = useRef<HTMLInputElement>(null);
	const { user } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
	const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
	const [ignoreConflicts, setIgnoreConflicts] = useState(false);
	const [rescheduleProposal, setRescheduleProposal] = useState<RescheduleTaskChange[]>([]);
	const [isCalculatingReschedule, setIsCalculatingReschedule] = useState(false);
	const [applyAutoReschedule, setApplyAutoReschedule] = useState(false);
	const [issues, setIssues] = useState<TaskIssue[]>([]);
	const [isLoadingIssues, setIsLoadingIssues] = useState(false);
	const [newIssueDescription, setNewIssueDescription] = useState('');
	const [newIssueSeverity, setNewIssueSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
	const [newIssueAttachmentId, setNewIssueAttachmentId] = useState<number | null>(null);
	const [isRequestingReplan, setIsRequestingReplan] = useState(false);
	const [replanResult, setReplanResult] = useState<ProjectReplanResponse | null>(null);
	const [editableReplan, setEditableReplan] = useState<ProjectReplanResponse['replan'] | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Use refs to store latest values for callbacks to avoid dependency issues
	const projectRef = useRef(project);
	const isDatabaseTaskRef = useRef(false);
	const taskIdRef = useRef<number | null>(null);
	
	// Update refs when values change
	useEffect(() => {
		projectRef.current = project;
	}, [project]);
	
	useEffect(() => {
		const isDbTask = task?.id.startsWith('db-');
		isDatabaseTaskRef.current = isDbTask || false;
		taskIdRef.current = isDbTask && task ? parseInt(task.id.replace('db-', '')) : null;
	}, [task]);

	// Track the current task ID so we only reinitialize when switching tasks
	const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);

	// Initialize task data when switching to a different task
	useEffect(() => {
		if (!task) {
			setTaskData(null);
			setCurrentTaskId(null);
			return;
		}

		const taskId = task.id;

		if (taskId !== currentTaskId) {
			setCurrentTaskId(taskId);
			setTaskData(taskToFormState(task));
			setNewNote('');
			setConflicts([]);
			setRescheduleProposal([]);
			setIsEditing(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [task?.id]);

	// Load attachments when task changes
	useEffect(() => {
		if (task) {
			const isDbTask = task.id.startsWith('db-');
			const tId = isDbTask ? parseInt(task.id.replace('db-', '')) : null;
			if (isDbTask && tId) {
				tasksAPI.getTaskAttachments(tId)
					.then(setAttachments)
					.catch(error => {
						console.error('Failed to load attachments:', error);
						setAttachments([]);
					});
				// Load issues for this task
				setIsLoadingIssues(true);
				tasksAPI.getTaskIssues(tId)
					.then(setIssues)
					.catch(error => {
						console.error('Failed to load task issues:', error);
						setIssues([]);
					})
					.finally(() => setIsLoadingIssues(false));
			} else {
				setAttachments([]);
				setIssues([]);
			}
		}
	}, [task]);

	// Handle ESC key and body scroll lock
	useEffect(() => {
		if (task) {
			document.body.style.overflow = 'hidden';
			const handleEsc = (e: KeyboardEvent) => {
				if (e.key === 'Escape' && !isEditing) {
					onClose();
				}
			};
			document.addEventListener('keydown', handleEsc);
			return () => {
				document.body.style.overflow = '';
				document.removeEventListener('keydown', handleEsc);
			};
		}
	}, [task, isEditing, onClose]);

	// Debounce timer for conflict checking - MUST be before early return
	const conflictCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	
	// Use refs to avoid dependency issues - these functions don't need to be in dependency arrays
	const checkConflicts = useCallback(async (startDate: string, endDate: string) => {
		const currentProject = projectRef.current;
		const currentIsDbTask = isDatabaseTaskRef.current;
		const currentTaskId = taskIdRef.current;
		
		if (!currentProject || !currentIsDbTask || !currentTaskId) return;
		
		setIsCheckingConflicts(true);
		setIgnoreConflicts(false);
		try {
			const result = await tasksAPI.checkTaskConflicts({
				project_id: typeof currentProject.id === 'string' ? parseInt(currentProject.id) : currentProject.id,
				start_date: new Date(startDate).toISOString(),
				end_date: new Date(endDate).toISOString(),
				exclude_task_id: currentTaskId // Exclude current task from conflict check
			});
			setConflicts(result.conflicts || []);
		} catch (err) {
			console.error('Failed to check conflicts:', err);
			// Don't show error to user, just silently fail
			setConflicts([]);
		} finally {
			setIsCheckingConflicts(false);
		}
	}, []); // Empty deps - uses refs instead

	const calculateReschedule = useCallback(async (startDate: string, endDate: string) => {
		const currentProject = projectRef.current;
		const currentIsDbTask = isDatabaseTaskRef.current;
		const currentTaskId = taskIdRef.current;
		
		if (!currentProject || !currentIsDbTask || !currentTaskId) return;
		
		setIsCalculatingReschedule(true);
		try {
			const result = await tasksAPI.calculateAutoReschedule({
				project_id: typeof currentProject.id === 'string' ? parseInt(currentProject.id) : currentProject.id,
				start_date: new Date(startDate).toISOString(),
				end_date: new Date(endDate).toISOString(),
				exclude_task_id: currentTaskId,
				reschedule_mode: 'shift_forward'
			});
			setRescheduleProposal(result.affected_tasks || []);
		} catch (err) {
			console.error('Failed to calculate reschedule:', err);
			setRescheduleProposal([]);
		} finally {
			setIsCalculatingReschedule(false);
		}
	}, []); // Empty deps - uses refs instead

	// Permissions
	const canEditCoreTaskFields =
		user?.role === 'company' || user?.role === 'gardener' || user?.role === 'admin';
	// Any authenticated user (including seeker) can add notes and attachments
	const canAddNotesAndAttachments = !!user;
	// Companies/admins can create issues and request AI re-plan
	const canCreateIssues = !!user && (user.role === 'company' || user.role === 'gardener' || user.role === 'admin' || user.role === 'seeker');
	const canRequestReplan = !!user && (user.role === 'company' || user.role === 'admin' || user.role === 'gardener');
	const canApplyReplan = !!user && (user.role === 'company' || user.role === 'admin');

	// Clear conflicts when not editing or when task changes
	useEffect(() => {
		if (!isEditing || !task) {
			setConflicts([]);
			setRescheduleProposal([]);
		}
	}, [isEditing, task?.id]);
	
	// Cleanup timeout on unmount - MUST be before early return
	useEffect(() => {
		return () => {
			if (conflictCheckTimeoutRef.current) {
				clearTimeout(conflictCheckTimeoutRef.current);
			}
		};
	}, []);

	// NOW we can do early return - all hooks are declared above
	if (!task || !taskData) return null;

	const isDatabaseTask = task.id.startsWith('db-');
	const taskId = isDatabaseTask ? parseInt(task.id.replace('db-', '')) : null;
	const hasInlineUnsavedChanges =
		!isEditing &&
		canEditCoreTaskFields &&
		isDatabaseTask &&
		!!taskId &&
		(
			(taskData.progress ?? 0) !== (task.progress ?? 0) ||
			(taskData.status || 'not_started') !== (task.status || 'not_started')
		);

	const handleAttemptClose = () => {
		if (hasInlineUnsavedChanges) {
			const discard = confirm(
				copy(
					'You have unsaved progress/status changes. Close without saving?',
					'Sie haben ungespeicherte Fortschritts-/Statusänderungen. Ohne Speichern schließen?',
					'Imate nespremljene promjene napretka/statusa. Zatvoriti bez spremanja?'
				)
			);
			if (!discard) return;
		}
		onClose();
	};

	// Load attachments for the task
	const loadAttachments = async () => {
		if (!task || !isDatabaseTask || !taskId) return;
		
		try {
			const loadedAttachments = await tasksAPI.getTaskAttachments(taskId);
			setAttachments(loadedAttachments);
		} catch (error) {
			console.error('Failed to load attachments:', error);
		}
	};

	const handleDelete = async () => {
		if (!taskId || !isDatabaseTask) return;
		if (!confirm('Are you sure you want to delete this task? This cannot be undone.')) return;
		setIsDeleting(true);
		try {
			await tasksAPI.deleteTask(taskId);
			onUpdate();
			onClose();
		} catch (err) {
			console.error('Failed to delete task:', err);
			alert(copy('Failed to delete task. Please try again.', 'Aufgabe konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.', 'Brisanje zadatka nije uspjelo. Pokušajte ponovno.'));
		} finally {
			setIsDeleting(false);
		}
	};

	const getStatusColor = (status?: string): string => {
		switch (status) {
			case 'completed': return 'success';
			case 'in_progress': return 'warning';
			case 'at_risk': return 'warning';
			case 'delayed': return 'danger';
			case 'blocked': return 'danger';
			default: return 'secondary';
		}
	};

	const getStatusLabel = (status?: string): string => {
		switch (status) {
			case 'completed': return copy('Completed', 'Abgeschlossen', 'Završeno');
			case 'in_progress': return copy('In Progress', 'In Bearbeitung', 'U tijeku');
			case 'at_risk': return copy('At Risk', 'Risikobehaftet', 'Rizično');
			case 'delayed': return copy('Delayed', 'Verzögert', 'Odgođeno');
			case 'blocked': return copy('Blocked', 'Blockiert', 'Blokirano');
			default: return copy('Not Started', 'Nicht begonnen', 'Nije započeto');
		}
	};

	const getIssueStatusLabel = (status?: string): string => {
		switch (status) {
			case 'open': return copy('Open', 'Offen', 'Otvoreno');
			case 'in_review': return copy('In review', 'In Prüfung', 'U pregledu');
			case 'resolved': return copy('Resolved', 'Gelöst', 'Riješeno');
			default: return status || copy('Unknown', 'Unbekannt', 'Nepoznato');
		}
	};

	const getIssueSeverityLabel = (severity?: string): string => {
		switch (severity) {
			case 'low': return copy('Low', 'Niedrig', 'Nisko');
			case 'medium': return copy('Medium', 'Mittel', 'Srednje');
			case 'high': return copy('High', 'Hoch', 'Visoko');
			case 'critical': return copy('Critical', 'Kritisch', 'Kritično');
			default: return severity || copy('Unknown', 'Unbekannt', 'Nepoznato');
		}
	};

	const getGardenerName = (gardenerId: string): string => {
		// Primary: look up in provided gardeners list (Calendar/Tasks views)
		const gardener = gardeners.find(g => String(g.id) === gardenerId);
		if (gardener) return gardener.name;

		// Fallback: use names embedded on the task itself (used in seeker project view)
		if (task?.gardenerNames && task.gardenerNames.length > 0) {
			const ids = task.gardenerIds || (task.gardenerId ? [task.gardenerId] : []);
			const index = ids.findIndex(id => String(id) === String(gardenerId));
			if (index !== -1 && task.gardenerNames[index]) {
				return task.gardenerNames[index]!;
			}
			// If we can't match by index, at least show the first name or all names
			if (task.gardenerNames.length === 1) {
				return task.gardenerNames[0]!;
			}
			return task.gardenerNames.join(', ');
		}

		return copy('Unknown', 'Unbekannt', 'Nepoznato');
	};

	// Manual conflict checking - only when dates are manually changed in edit mode
	const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
		if (!taskData) return;
		
		// Clear any pending conflict checks
		if (conflictCheckTimeoutRef.current) {
			clearTimeout(conflictCheckTimeoutRef.current);
		}
		
		setTaskData(prev => prev ? { ...prev, [field]: value } : null);
		
		// Only check conflicts if both dates are set and valid, and we're in edit mode
		if (!isEditing) return;
		
		const newStartDate = field === 'startDate' ? value : taskData.startDate;
		const newEndDate = field === 'endDate' ? value : taskData.endDate;
		
		if (newStartDate && newEndDate && newStartDate <= newEndDate) {
			// Debounce conflict checking
			conflictCheckTimeoutRef.current = setTimeout(() => {
				checkConflicts(newStartDate, newEndDate);
				calculateReschedule(newStartDate, newEndDate);
			}, 500);
		} else {
			setConflicts([]);
			setRescheduleProposal([]);
		}
	};

	const handleSave = async () => {
		// Only company / gardener / admin can change core task fields
		if (!canEditCoreTaskFields || !taskData) return;

		// Check for conflicts if not already ignored and not applying auto-reschedule
		if (!ignoreConflicts && !applyAutoReschedule && conflicts.length > 0) {
			const proceed = confirm(
				`Warning: This task overlaps with ${conflicts.length} other task(s). ` +
				`This may cause scheduling conflicts. Do you want to proceed anyway?`
			);
			if (!proceed) {
				return;
			}
		}

		setIsSaving(true);
		try {
			if (isDatabaseTask && taskId) {
				const startDay = taskData.startDate.split('T')[0];
				const endDay = taskData.endDate.split('T')[0];
				const startNoon = new Date(`${startDay}T12:00:00`);
				const endNoon = new Date(`${endDay}T12:00:00`);
				const durationDays = Math.max(
					1,
					Math.ceil((endNoon.getTime() - startNoon.getTime()) / (1000 * 60 * 60 * 24)) + 1
				);

				const before = await tasksAPI.getTaskById(taskId);

				const updates: tasksAPI.UpdateTaskPayload = {
					title: taskData.title,
					description: taskData.description || undefined,
					status: taskData.status,
					progress: taskData.progress,
					start_date: startDay,
					end_date: endDay,
					due_date: endDay,
					duration: durationDays,
					notes: taskData.notes || undefined
				};

				await tasksAPI.updateTask(taskId, updates);

				// PHP task PUT ignores gardener_ids; sync assignees via task endpoints
				const prevG = new Set(before.gardener_ids.map(String));
				const nextG = new Set(taskData.selectedGardeners.map(String));
				for (const gid of nextG) {
					if (!prevG.has(gid)) {
						const n = parseInt(gid, 10);
						if (!Number.isNaN(n)) await tasksAPI.assignGardenerToTask(taskId, n);
					}
				}
				for (const gid of prevG) {
					if (!nextG.has(gid)) {
						const n = parseInt(gid, 10);
						if (!Number.isNaN(n)) await tasksAPI.removeGardenerFromTask(taskId, n);
					}
				}

				// Keep modal state as source of truth immediately after save.
				// Some backends briefly return stale values on a follow-up read.
				onTaskPatched?.(taskId, {
					title: taskData.title,
					description: taskData.description || null,
					status: taskData.status,
					progress: taskData.progress,
					start_date: startDay,
					end_date: endDay,
					due_date: endDay,
					duration: durationDays,
					notes: taskData.notes || null,
					gardener_ids: taskData.selectedGardeners.map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n))
				} as Partial<TaskResponse>);
				// Apply auto-rescheduling if requested
				if (applyAutoReschedule && rescheduleProposal.length > 0) {
					try {
						await tasksAPI.applyAutoReschedule({
							task_changes: rescheduleProposal,
							reason: `Auto-rescheduled due to update of task: ${taskData.title}`
						});
					} catch (rescheduleErr) {
						console.error('Failed to apply auto-reschedule:', rescheduleErr);
						alert('Task updated successfully, but auto-rescheduling failed. Please reschedule manually.');
					}
				}
			}
			await Promise.resolve(onUpdate());
			setIsEditing(false);
		} catch (error) {
			console.error('Failed to update task:', error);
			alert('Failed to update task. Please try again.');
		} finally {
			setIsSaving(false);
		}
	};

	const handleProgressUpdate = (newProgress: number) => {
		// Only company / gardener / admin can stage progress changes
		if (!canEditCoreTaskFields || !isDatabaseTask || !taskId || !taskData) return;
		let newStatus = taskData.status;
		if (newProgress > 0 && taskData.status === 'not_started') {
			newStatus = 'in_progress';
		} else if (newProgress === 100 && taskData.status !== 'completed') {
			newStatus = 'completed';
		}
		setTaskData(prev => prev ? {
			...prev,
			progress: newProgress,
			status: newStatus
		} : null);
	};

	const handleStatusUpdate = (newStatus: GanttTask['status']) => {
		// Only company / gardener / admin can stage status changes
		if (!canEditCoreTaskFields || !isDatabaseTask || !taskId || !taskData) return;

		// Auto-sync: If status is "completed", set progress to 100%
		let newProgress = taskData.progress;
		if (newStatus === 'completed' && taskData.progress < 100) {
			newProgress = 100;
		}

		setTaskData(prev => prev ? { 
			...prev, 
			status: newStatus,
			progress: newProgress
		} : null);
	};

	const handleProgressChange = (newProgress: number) => {
		// Only company / gardener / admin can stage progress changes
		if (!canEditCoreTaskFields || !isDatabaseTask || !taskId || !taskData) return;

		let newStatus = taskData.status;
		
		if (newProgress > 0 && taskData.status === 'not_started') {
			newStatus = 'in_progress';
		} else if (newProgress === 100 && taskData.status !== 'completed') {
			newStatus = 'completed';
		}

		setTaskData(prev => prev ? { 
			...prev, 
			progress: newProgress,
			status: newStatus
		} : null);
	};

	const saveInlineProgressAndStatus = async () => {
		if (!canEditCoreTaskFields || !isDatabaseTask || !taskId || !taskData || !hasInlineUnsavedChanges) return;

		setIsSaving(true);
		try {
			await tasksAPI.updateTask(taskId, {
				progress: taskData.progress,
				status: taskData.status
			} as any);
			onTaskPatched?.(taskId, {
				progress: taskData.progress,
				status: taskData.status
			} as Partial<TaskResponse>);
			// Refresh parent once after explicit save to keep all derived fields consistent.
			await Promise.resolve(onUpdate());
		} catch (error) {
			console.error('Failed to save inline task changes:', error);
			alert('Failed to save changes. Please try again.');
			if (task) {
				setTaskData(prev => prev ? { 
					...prev, 
					progress: task.progress || 0,
					status: task.status || 'not_started'
				} : null);
			}
		} finally {
			setIsSaving(false);
		}
	};

	const handleAddNote = async () => {
		if (!canAddNotesAndAttachments || !newNote.trim() || !isDatabaseTask || !taskId) return;

		const noteText = newNote.trim();
		const timestamp = new Date().toLocaleString();
		const authorName = (user && (user.name || (user as any).display_name || user.email)) || 'Someone';
		const roleLabel = user?.role ? ` (${user.role})` : '';
		const formattedNote = `[${timestamp}] ${authorName}${roleLabel}: ${noteText}\n`;
		const updatedNotes = (taskData?.notes || '') + formattedNote;

		setIsSaving(true);
		try {
			await tasksAPI.updateTask(taskId, { notes: updatedNotes } as any);
			setTaskData(prev => prev ? { ...prev, notes: updatedNotes } : null);
			setNewNote('');
			onUpdate();
		} catch (error) {
			console.error('Failed to add note:', error);
			alert('Failed to add note. Please try again.');
		} finally {
			setIsSaving(false);
		}
	};

	const handleFileSelect = () => {
		fileInputRef.current?.click();
	};

	/**
	 * The backend for task attachments doesn't accept HEIC/HEIF.
	 * iOS camera capture often produces those, so we convert to JPEG when needed.
	 */
	async function convertImageIfUnsupported(file: File): Promise<File> {
		const mime = (file.type || '').toLowerCase();
		const name = (file.name || '').toLowerCase();

		// Backend-allowed images: image/jpeg, image/png, image/gif, image/webp, image/svg+xml (extension-based)
		const isHeicLike = mime.includes('heic') || mime.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
		if (!isHeicLike) return file;

		try {
			const heic2anyModule = await import('heic2any');
			const heic2any = heic2anyModule.default as (options: { blob: Blob; toType: string; quality?: number }) => Promise<Blob | Blob[]>;
			const converted = await heic2any({
				blob: file,
				toType: 'image/jpeg',
				quality: 0.9
			});
			const blob = Array.isArray(converted) ? converted[0] : converted;
			if (!blob) return file;
			const baseName = file.name.replace(/\.[^.]+$/, '');
			return new File([blob], `${baseName || 'photo'}.jpg`, { type: 'image/jpeg' });
		} catch (err) {
			console.warn('Failed to convert HEIC/HEIF to JPEG; uploading original.', err);
			return file;
		}
	}

	const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file || !canAddNotesAndAttachments || !isDatabaseTask || !taskId) return;

		setIsUploading(true);
		try {
			const fileToUpload = await convertImageIfUnsupported(file);
			const uploadedBy = user?.id ? parseInt(String(user.id)) : undefined;
			const attachment = await tasksAPI.uploadTaskAttachment(
				taskId,
				fileToUpload,
				uploadDescription.trim() || undefined,
				uploadedBy
			);
			setAttachments(prev => [attachment, ...prev]);
			setUploadDescription('');
			// Reset whichever input triggered the upload so the same photo can be taken again.
			e.target.value = '';
			if (fileInputRef.current) fileInputRef.current.value = '';
			if (cameraInputRef.current) cameraInputRef.current.value = '';
			onUpdate();
		} catch (error) {
			console.error('Failed to upload file:', error);
			alert('Failed to upload file. Please try again.');
		} finally {
			setIsUploading(false);
		}
	};

	const handleDeleteAttachment = async (attachmentId: number) => {
		if (!canAddNotesAndAttachments || !isDatabaseTask || !taskId) return;
		if (!confirm('Are you sure you want to delete this file?')) return;

		setIsSaving(true);
		try {
			await tasksAPI.deleteTaskAttachment(taskId, attachmentId);
			setAttachments(prev => prev.filter(a => a.id !== attachmentId));
			onUpdate();
		} catch (error) {
			console.error('Failed to delete attachment:', error);
			alert('Failed to delete file. Please try again.');
		} finally {
			setIsSaving(false);
		}
	};

	const handleCreateIssue = async () => {
		if (!task || !isDatabaseTask || !taskId) return;
		const desc = newIssueDescription.trim();
		if (!desc) return;

		setIsSaving(true);
		try {
			const createdByUserId = user?.id ? (typeof user.id === 'string' ? parseInt(user.id, 10) : user.id) : undefined;
			const payload: tasksAPI.CreateTaskIssuePayload = {
				description: desc,
				severity: newIssueSeverity,
				attachment_id: newIssueAttachmentId ?? undefined,
				created_by_user_id: createdByUserId ?? undefined
			};
			const issue = await tasksAPI.createTaskIssue(taskId, payload);
			setIssues(prev => [issue, ...prev]);
			setNewIssueDescription('');
			setNewIssueAttachmentId(null);
			// Refresh outer data so Tasks list can react if needed
			onUpdate();
		} catch (error) {
			console.error('Failed to create task issue:', error);
			alert('Failed to create issue. Please try again.');
		} finally {
			setIsSaving(false);
		}
	};

	const handleRequestReplan = async () => {
		if (!project || !task || !isDatabaseTask || !taskId || !canRequestReplan) return;
		const openIssues = issues.filter(i => i.status === 'open' || i.status === 'in_review');
		if (openIssues.length === 0) {
			alert('There are no open issues for this task. Please log an issue first.');
			return;
		}

		setIsRequestingReplan(true);
		try {
			const projectIdNum = typeof project.id === 'string' ? parseInt(project.id, 10) : project.id;
			const userIdNum = user?.id ? (typeof user.id === 'string' ? parseInt(user.id, 10) : user.id) : undefined;
			const response = await requestProjectReplan(projectIdNum, {
				task_id: taskId,
				issue_ids: openIssues.map(i => i.id),
				user_id: userIdNum,
				ui_language: language
			});
			setReplanResult(response);
			// Deep clone to editable state
			setEditableReplan(JSON.parse(JSON.stringify(response.replan)) as ProjectReplanResponse['replan']);
		} catch (error) {
			console.error('Failed to request re-plan:', error);
			alert('Re-plan failed. Please try again or check server logs.');
		} finally {
			setIsRequestingReplan(false);
		}
	};

	const getFileIcon = (mimeType?: string) => {
		if (!mimeType) return 'bi-file-earmark';
		if (mimeType.startsWith('image/')) return 'bi-file-image';
		if (mimeType === 'application/pdf') return 'bi-file-pdf';
		if (mimeType.includes('word') || mimeType.includes('document')) return 'bi-file-word';
		if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'bi-file-excel';
		if (mimeType.includes('zip') || mimeType.includes('rar')) return 'bi-file-zip';
		return 'bi-file-earmark';
	};

	const baseApiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/api';

	/** URL to view or download a task attachment (by ID). Use forPreview=false and download attr for download. */
	const getAttachmentFileUrl = (attachmentId: number, forDownload: boolean = false) => {
		if (!taskId) return '#';
		const url = `${baseApiUrl.replace(/\/$/, '')}/tasks/${taskId}/attachments/${attachmentId}/file`;
		return forDownload ? `${url}?download=1` : url;
	};

	const assignedGardeners = taskData.selectedGardeners.map(id => ({
		id,
		name: getGardenerName(id)
	}));

	const canUseEditMode = canEditCoreTaskFields;
	const sectionCardClass = 'card border-0 shadow-sm rounded-4';
	const hasTaskAdjustments = Boolean(editableReplan?.task_adjustments && editableReplan.task_adjustments.length > 0);
	const hasSuggestedNewTasks = Boolean(editableReplan?.new_tasks && editableReplan.new_tasks.length > 0);
	const showTimelineChanges = Boolean(
		editableReplan?.timeline_changes &&
		editableReplan.timeline_changes.length > 0 &&
		(!hasTaskAdjustments || hasSuggestedNewTasks)
	);
	const projectDetailPath = project
		? `/app/${user?.role === 'seeker' ? 'seeker' : 'gardener'}/project/${project.id}`
		: null;
	const modalDisplayTitle = (() => {
		const rawTitle = (taskData.title || '').trim();
		const rawDescription = (taskData.description || '').trim();
		if (!rawTitle.endsWith('...')) return rawTitle;
		if (!rawDescription) return rawTitle;
		// Heuristic for legacy truncated titles: use first sentence from description when title is ellipsized.
		const sentenceEnd = rawDescription.search(/[.!?](\s|$)/);
		if (sentenceEnd > 0) {
			return rawDescription.slice(0, sentenceEnd + 1).trim();
		}
		return rawDescription;
	})();
	const legalMeta = task
		? getTaskLegalMeta(task, companyCountryCode)
		: null;

	return (
		<>
			<div
				className="modal fade show"
				style={{ display: 'block', zIndex: 20000 }}
				tabIndex={-1}
				onClick={(e) => {
					if (e.target === e.currentTarget && !isEditing) {
						onClose();
					}
				}}
			>
				<div
					className="modal-dialog modal-xl modal-dialog-scrollable"
					onClick={(e) => e.stopPropagation()}
				>
					<div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header bg-light border-bottom px-4 py-3 align-items-start">
							<div className="flex-grow-1 pe-3">
								<h2
									className="fw-bold text-success-emphasis mb-2"
									style={{
										whiteSpace: 'normal',
										overflowWrap: 'anywhere',
										wordBreak: 'break-word',
										lineHeight: 1.2
									}}
								>
									{isEditing ? copy('Edit Task', 'Aufgabe bearbeiten', 'Uredi zadatak') : modalDisplayTitle}
								</h2>
								<TaskLegalBadge legalMeta={legalMeta} copy={copy} />
								{legalMeta?.reason ? (
									<div className="small text-muted mt-2">{legalMeta.reason}</div>
								) : null}
							</div>
							<button
								type="button"
								className="btn-close"
								onClick={onClose}
								disabled={isSaving}
								aria-label="Close"
							></button>
						</div>
						<div className="modal-body bg-body-tertiary px-4 py-4">
							{getStoredCompanyScopeMode() === 'all_companies' &&
								(user?.companyIds?.length ?? 0) > 1 &&
								(project?.companyName || task.projectCompanyName) && (
									<div className="alert alert-light border py-2 small mb-3" role="status">
										{copy('Company', 'Unternehmen', 'Tvrtka')}:{' '}
										<strong>{project?.companyName || task.projectCompanyName}</strong>
									</div>
								)}
							{/* Task title editor appears only in edit mode */}
							{isEditing ? (
								<div className="mb-4">
									<label className="form-label fw-semibold">{copy('Task Title', 'Aufgabentitel', 'Naslov zadatka')}</label>
									<input
										type="text"
										className="form-control"
										value={taskData.title}
										onChange={(e) => setTaskData(prev => prev ? { ...prev, title: e.target.value } : null)}
										disabled={isSaving}
									/>
								</div>
							) : null}

							{/* Unified Status + Progress */}
							<div className="mb-4">
								<div className={sectionCardClass}>
									<div className="card-body p-4">
										<div className="d-flex align-items-center justify-content-between mb-3">
											<h4 className="h6 fw-bold text-success-emphasis mb-0">
												{copy('Status & Progress', 'Status & Fortschritt', 'Status i napredak')}
											</h4>
											<span className={`badge text-bg-${getStatusColor(taskData.status)} fs-6`}>
												{getStatusLabel(taskData.status)}
											</span>
										</div>
										<div className="row g-4">
											<div className="col-12 col-md-6">
												<h5 className="small text-uppercase text-muted mb-2">{copy('Status', 'Status', 'Status')}</h5>
												{isEditing ? (
													<select
														className="form-select"
														value={taskData.status}
														onChange={(e) => setTaskData(prev => prev ? { ...prev, status: e.target.value as GanttTask['status'] } : null)}
														disabled={isSaving}
													>
														<option value="not_started">{copy('Not Started', 'Nicht begonnen', 'Nije započeto')}</option>
														<option value="in_progress">{copy('In Progress', 'In Bearbeitung', 'U tijeku')}</option>
														<option value="completed">{copy('Completed', 'Abgeschlossen', 'Završeno')}</option>
														<option value="at_risk">{copy('At Risk', 'Risikobehaftet', 'Rizično')}</option>
														<option value="delayed">{copy('Delayed', 'Verzögert', 'Odgođeno')}</option>
														<option value="blocked">{copy('Blocked', 'Blockiert', 'Blokirano')}</option>
													</select>
												) : (
													<div>
														<label className="form-label small">{copy('Quick Status Update', 'Schnelles Status-Update', 'Brzo ažuriranje statusa')}</label>
														<div className="btn-group w-100" role="group">
															<button
																type="button"
																className={`btn btn-sm ${taskData.status === 'not_started' ? 'btn-secondary' : 'btn-outline-secondary'}`}
																onClick={() => {
																	if (canEditCoreTaskFields) {
																		handleStatusUpdate('not_started');
																	}
																}}
																disabled={isSaving || !canEditCoreTaskFields}
															>
																{copy('Not Started', 'Nicht begonnen', 'Nije započeto')}
															</button>
															<button
																type="button"
																className={`btn btn-sm ${taskData.status === 'in_progress' ? 'btn-warning' : 'btn-outline-warning'}`}
																onClick={() => {
																	if (canEditCoreTaskFields) {
																		handleStatusUpdate('in_progress');
																	}
																}}
																disabled={isSaving || !canEditCoreTaskFields}
															>
																{copy('In Progress', 'In Bearbeitung', 'U tijeku')}
															</button>
															<button
																type="button"
																className={`btn btn-sm ${taskData.status === 'completed' ? 'btn-success' : 'btn-outline-success'}`}
																onClick={() => {
																	if (canEditCoreTaskFields) {
																		handleStatusUpdate('completed');
																	}
																}}
																disabled={isSaving || !canEditCoreTaskFields}
															>
																{copy('Completed', 'Abgeschlossen', 'Završeno')}
															</button>
														</div>
													</div>
												)}
											</div>
											<div className="col-12 col-md-6">
												<h5 className="small text-uppercase text-muted mb-2">{copy('Progress', 'Fortschritt', 'Napredak')}</h5>
												{isEditing ? (
													<>
														<input
															type="range"
															className="form-range mb-2"
															min="0"
															max="100"
															value={taskData.progress}
															onChange={(e) => setTaskData(prev => prev ? { ...prev, progress: parseInt(e.target.value) } : null)}
															disabled={isSaving}
														/>
														<div className="d-flex justify-content-between align-items-center">
															<span className="small text-muted">0%</span>
															<span className="fw-bold">{taskData.progress}%</span>
															<span className="small text-muted">100%</span>
														</div>
													</>
												) : (
													<>
														<input
															type="range"
															className="form-range mb-2"
															min="0"
															max="100"
															value={taskData.progress}
															onChange={(e) => {
																if (canEditCoreTaskFields) {
																	handleProgressChange(parseInt(e.target.value));
																}
															}}
															disabled={isSaving || !canEditCoreTaskFields}
														/>
														<div className="d-flex justify-content-between align-items-center mb-3">
															<span className="small text-muted">0%</span>
															<span className="fw-bold">{taskData.progress}%</span>
															<span className="small text-muted">100%</span>
														</div>
														<div className="progress mb-3 rounded-pill" style={{ height: '24px' }}>
															<div
																className={`progress-bar bg-${getStatusColor(taskData.status)}`}
																role="progressbar"
																style={{ width: `${taskData.progress}%` }}
																aria-valuenow={taskData.progress}
																aria-valuemin={0}
																aria-valuemax={100}
															>
																{taskData.progress}%
															</div>
														</div>
														<div className="d-flex gap-2 flex-wrap">
															<button
																type="button"
																className={`btn btn-sm ${taskData.progress === 25 ? 'btn-success' : 'btn-outline-success'}`}
																onClick={() => {
																	if (canEditCoreTaskFields) {
																		handleProgressChange(25);
																	}
																}}
																disabled={isSaving || !canEditCoreTaskFields}
															>
																25%
															</button>
															<button
																type="button"
																className={`btn btn-sm ${taskData.progress === 50 ? 'btn-success' : 'btn-outline-success'}`}
																onClick={() => {
																	if (canEditCoreTaskFields) {
																		handleProgressChange(50);
																	}
																}}
																disabled={isSaving || !canEditCoreTaskFields}
															>
																50%
															</button>
															<button
																type="button"
																className={`btn btn-sm ${taskData.progress === 75 ? 'btn-success' : 'btn-outline-success'}`}
																onClick={() => {
																	if (canEditCoreTaskFields) {
																		handleProgressChange(75);
																	}
																}}
																disabled={isSaving || !canEditCoreTaskFields}
															>
																75%
															</button>
															<button
																type="button"
																className={`btn btn-sm ${taskData.progress === 100 ? 'btn-success' : 'btn-outline-success'}`}
																onClick={() => {
																	if (canEditCoreTaskFields) {
																		handleProgressChange(100);
																	}
																}}
																disabled={isSaving || !canEditCoreTaskFields}
															>
																100%
															</button>
														</div>
													</>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Task Details */}
							<div className="row g-4">
								<div className="col-12 col-lg-8">
									{/* Description */}
									<div className={`${sectionCardClass} mb-4`}>
										<div className="card-body p-4">
											<h4 className="h6 fw-bold text-success-emphasis mb-3">{copy('Description', 'Beschreibung', 'Opis')}</h4>
											{isEditing ? (
												<textarea
													className="form-control"
													rows={4}
													value={taskData.description}
													onChange={(e) => setTaskData(prev => prev ? { ...prev, description: e.target.value } : null)}
													disabled={isSaving}
													placeholder={copy('Enter task description...', 'Aufgabenbeschreibung eingeben...', 'Unesite opis zadatka...')}
												/>
											) : (
												<p className="text-muted mb-0">
													{taskData.description || <em className="text-muted">{copy('No description provided', 'Keine Beschreibung vorhanden', 'Nema opisa')}</em>}
												</p>
											)}
										</div>
									</div>

									{/* Dates */}
									<div className={`${sectionCardClass} mb-4`}>
										<div className="card-body p-4">
											<h4 className="h6 fw-bold text-success-emphasis mb-3">{copy('Timeline', 'Zeitplan', 'Vremenska crta')}</h4>
											<div className="row g-3">
												<div className="col-12 col-md-6">
													<div className="text-muted small">{copy('Start Date', 'Startdatum', 'Datum početka')}</div>
													{isEditing ? (
														<input
															type="date"
															className="form-control"
															value={taskData.startDate}
															onChange={(e) => handleDateChange('startDate', e.target.value)}
															disabled={isSaving}
														/>
													) : (
														<div className="fw-semibold">
															{new Date(taskData.startDate).toLocaleDateString('en-GB', {
																year: 'numeric',
																month: 'long',
																day: 'numeric'
															})}
														</div>
													)}
												</div>
												<div className="col-12 col-md-6">
													<div className="text-muted small">{copy('End Date', 'Enddatum', 'Datum završetka')}</div>
													{isEditing ? (
														<input
															type="date"
															className="form-control"
															value={taskData.endDate}
															onChange={(e) => handleDateChange('endDate', e.target.value)}
															disabled={isSaving}
														/>
													) : (
														<div className="fw-semibold">
															{new Date(taskData.endDate).toLocaleDateString('en-GB', {
																year: 'numeric',
																month: 'long',
																day: 'numeric'
															})}
														</div>
													)}
												</div>
												<div className="col-12">
													<div className="text-muted small">{copy('Duration', 'Dauer', 'Trajanje')}</div>
													<div className="fw-semibold">
														{Math.ceil((new Date(taskData.endDate).getTime() - new Date(taskData.startDate).getTime()) / (1000 * 60 * 60 * 24))} {copy('days', 'Tage', 'dana')}
													</div>
												</div>
											</div>

											{/* Conflict Warning (only show when editing) */}
											{isEditing && isCheckingConflicts && (
												<div className="alert alert-info mt-3 mb-0">
													<i className="bi bi-hourglass-split me-2"></i>
													{copy('Checking for scheduling conflicts...', 'Prüfe Terminüberschneidungen...', 'Provjera konflikata rasporeda...')}
												</div>
											)}
											{isEditing && !isCheckingConflicts && conflicts.length > 0 && (
												<div className="alert alert-warning mt-3 mb-0">
													<div className="d-flex align-items-start">
														<i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
														<div className="flex-grow-1">
															<strong>{copy('Schedule conflict detected!', 'Terminüberschneidung erkannt!', 'Otkriven konflikt rasporeda!')}</strong>
															<p className="mb-2 mt-2">
																{copy('This task overlaps with', 'Diese Aufgabe überschneidet sich mit', 'Ovaj zadatak se preklapa s')} {conflicts.length} {copy('other task(s) in this project:', 'anderen Aufgabe(n) in diesem Projekt:', 'drugim zadatkom/zadacima u ovom projektu:')}
															</p>
															<ul className="mb-2">
																{conflicts.map((conflict) => (
																	<li key={conflict.id}>
																		<strong>{conflict.title}</strong> ({conflict.status})
																		<br />
																		<small className="text-muted">
																			{new Date(conflict.start_date).toLocaleDateString()} - {new Date(conflict.end_date).toLocaleDateString()}
																			{conflict.gardener_names.length > 0 && (
																				<> • {copy('Assigned to', 'Zugewiesen an', 'Dodijeljeno')}: {conflict.gardener_names.join(', ')}</>
																			)}
																		</small>
																	</li>
																))}
															</ul>
														</div>
													</div>
												</div>
											)}

											{/* Auto-Reschedule Proposal */}
											{isEditing && isCalculatingReschedule && (
												<div className="alert alert-info mt-3 mb-0">
													<i className="bi bi-hourglass-split me-2"></i>
													{copy('Calculating auto-reschedule options...', 'Optionen für automatische Neuplanung werden berechnet...', 'Izračun mogućnosti automatskog pomicanja rasporeda...')}
												</div>
											)}
											{isEditing && !isCalculatingReschedule && rescheduleProposal.length > 0 && (
												<div className="alert alert-success mt-3 mb-0">
													<div className="d-flex align-items-start">
														<i className="bi bi-calendar-check me-2 mt-1"></i>
														<div className="flex-grow-1">
															<strong>{copy('Auto-reschedule available!', 'Automatische Neuplanung verfügbar!', 'Dostupno automatsko pomicanje rasporeda!')}</strong>
															<p className="mb-2 mt-2">
																{copy('We can automatically reschedule', 'Wir können', 'Možemo automatski pomaknuti')} {rescheduleProposal.length} {copy('affected task(s) to make room for this task:', 'betroffene Aufgabe(n) automatisch neu planen, um Platz für diese Aufgabe zu schaffen:', 'zahvaćenih zadataka kako bismo napravili mjesto za ovaj zadatak:')}
															</p>
															<div className="table-responsive mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
																<table className="table table-sm table-bordered">
																	<thead>
																		<tr>
																			<th>{copy('Task', 'Aufgabe', 'Zadatak')}</th>
																			<th>{copy('Old dates', 'Alte Termine', 'Stari datumi')}</th>
																			<th>{copy('New dates', 'Neue Termine', 'Novi datumi')}</th>
																			<th>{copy('Shift', 'Verschiebung', 'Pomak')}</th>
																		</tr>
																	</thead>
																	<tbody>
																		{rescheduleProposal.map((change) => (
																			<tr key={change.task_id}>
																				<td><strong>{change.task_title}</strong></td>
																				<td>
																					<small>
																						{new Date(change.old_start_date).toLocaleDateString()} - {new Date(change.old_end_date).toLocaleDateString()}
																					</small>
																				</td>
																				<td>
																					<small className="text-success">
																						{new Date(change.new_start_date).toLocaleDateString()} - {new Date(change.new_end_date).toLocaleDateString()}
																					</small>
																				</td>
																				<td>
																					<small className="text-muted">
																						{change.shift_days > 0 ? '+' : ''}{change.shift_days} {copy('days', 'Tage', 'dana')}
																					</small>
																				</td>
																			</tr>
																		))}
																	</tbody>
																</table>
															</div>
															<div className="form-check">
																<input
																	className="form-check-input"
																	type="checkbox"
																	id="applyAutoRescheduleEdit"
																	checked={applyAutoReschedule}
																	onChange={(e) => {
																		setApplyAutoReschedule(e.target.checked);
																		if (e.target.checked) {
																			setIgnoreConflicts(false);
																		}
																	}}
																/>
																<label className="form-check-label" htmlFor="applyAutoRescheduleEdit">
																	<strong>{copy('Apply auto-rescheduling', 'Automatische Neuplanung anwenden', 'Primijeni automatsko pomicanje')}</strong> - {copy('Automatically shift affected tasks when saving', 'Betroffene Aufgaben beim Speichern automatisch verschieben', 'Automatski pomakni zahvaćene zadatke pri spremanju')}
																</label>
															</div>
															{!applyAutoReschedule && (
																<div className="form-check mt-2">
																	<input
																		className="form-check-input"
																		type="checkbox"
																		id="ignoreConflictsEdit"
																		checked={ignoreConflicts}
																		onChange={(e) => {
																			setIgnoreConflicts(e.target.checked);
																			if (e.target.checked) {
																				setApplyAutoReschedule(false);
																			}
																		}}
																	/>
																	<label className="form-check-label" htmlFor="ignoreConflictsEdit">
																		{copy('I understand the conflict and want to proceed anyway (no auto-rescheduling)', 'Ich verstehe den Konflikt und möchte trotzdem fortfahren (ohne automatische Neuplanung)', 'Razumijem konflikt i želim svejedno nastaviti (bez automatskog pomicanja)')}
																	</label>
																</div>
															)}
														</div>
													</div>
												</div>
											)}
											{isEditing && !isCalculatingReschedule && rescheduleProposal.length === 0 && conflicts.length > 0 && (
												<div className="form-check mt-3">
													<input
														className="form-check-input"
														type="checkbox"
														id="ignoreConflictsEditFallback"
														checked={ignoreConflicts}
														onChange={(e) => setIgnoreConflicts(e.target.checked)}
													/>
													<label className="form-check-label" htmlFor="ignoreConflictsEditFallback">
														{copy('I understand the conflict and want to proceed anyway', 'Ich verstehe den Konflikt und möchte trotzdem fortfahren', 'Razumijem konflikt i želim svejedno nastaviti')}
													</label>
												</div>
											)}
										</div>
									</div>

									{/* Notes/Comments Section */}
									<div className={`${sectionCardClass} mb-4`}>
										<div className="card-body p-4">
											<h4 className="h6 fw-bold text-success-emphasis mb-3">
												<i className="bi bi-chat-left-text me-2"></i>{copy('Notes & Comments', 'Notizen & Kommentare', 'Bilješke i komentari')}
											</h4>
											{taskData.notes ? (
												<div className="mb-3 p-3 bg-light-subtle rounded-3 border" style={{ maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
													{taskData.notes}
												</div>
											) : (
												<p className="text-muted small mb-3">
													<em>{copy('No notes yet. Add a note to track important information about this task.', 'Noch keine Notizen. Fügen Sie eine Notiz hinzu, um wichtige Informationen zu verfolgen.', 'Još nema bilješki. Dodajte bilješku za praćenje važnih informacija o zadatku.')}</em>
												</p>
											)}
											<div className="d-flex gap-2">
												<input
													type="text"
													className="form-control form-control-sm"
													placeholder={copy('Add a note or comment...', 'Notiz oder Kommentar hinzufügen...', 'Dodaj bilješku ili komentar...')}
													value={newNote}
													onChange={(e) => setNewNote(e.target.value)}
													onKeyPress={(e) => {
														if (e.key === 'Enter' && !e.shiftKey) {
															e.preventDefault();
															handleAddNote();
														}
													}}
													disabled={isSaving || !isDatabaseTask || !canAddNotesAndAttachments}
												/>
												<button
													type="button"
													className="btn btn-sm btn-success"
													onClick={handleAddNote}
													disabled={
														isSaving ||
														!newNote.trim() ||
														!isDatabaseTask ||
														!canAddNotesAndAttachments
													}
												>
													<i className="bi bi-plus-circle me-1"></i>{copy('Add', 'Hinzufügen', 'Dodaj')}
												</button>
											</div>
										</div>
									</div>

									{/* Issues Section */}
									<div className={`${sectionCardClass} mb-4`}>
										<div className="card-body p-4">
											<h4 className="h6 fw-bold text-danger-emphasis mb-3">
												<i className="bi bi-exclamation-octagon me-2"></i>{copy('Issues that affect the plan', 'Probleme, die den Plan beeinflussen', 'Problemi koji utječu na plan')}
											</h4>
											<p className="text-muted small">
												{copy(
													"Report an issue when something on site could delay this task, increase cost, or change scope (for example hidden damage, access problems, or unexpected ground conditions). Add a clear description, choose severity, and attach a photo if available. If you request and apply a re-plan, you can still revert it later using the Undo button in the project timeline's re-plan history.",
													"Melden Sie ein Problem, wenn etwas auf der Baustelle diese Aufgabe verzögern, Kosten erhöhen oder den Umfang ändern könnte (z. B. versteckte Schäden, Zugangsprobleme oder unerwartete Bodenverhältnisse). Fügen Sie eine klare Beschreibung hinzu, wählen Sie den Schweregrad und hängen Sie nach Möglichkeit ein Foto an. Wenn Sie eine Neuplanung anfordern und anwenden, können Sie sie später über die Schaltfläche 'Rückgängig' im Neuplanungsverlauf der Projektzeitleiste zurücksetzen.",
													"Prijavite problem kada nešto na lokaciji može odgoditi ovaj zadatak, povećati trošak ili promijeniti opseg (npr. skrivena oštećenja, problemi s pristupom ili neočekivani uvjeti tla). Dodajte jasan opis, odaberite ozbiljnost i priložite fotografiju ako je dostupna. Ako zatražite i primijenite ponovno planiranje, i dalje ga možete kasnije vratiti pomoću gumba 'Poništi' u povijesti ponovnog planiranja vremenske crte projekta."
												)}
											</p>

											{isLoadingIssues ? (
												<div className="text-muted small">
													<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
													{copy('Loading issues...', 'Lade Probleme...', 'Učitavanje problema...')}
												</div>
											) : issues.length === 0 ? (
												<p className="text-muted small mb-3">
													<em>{copy('No issues logged for this task yet.', 'Für diese Aufgabe sind noch keine Probleme erfasst.', 'Za ovaj zadatak još nema prijavljenih problema.')}</em>
												</p>
											) : (
												<div className="mb-3" style={{ maxHeight: '220px', overflowY: 'auto' }}>
													<ul className="list-group list-group-flush">
														{issues.map(issue => (
															<li key={issue.id} className="list-group-item px-0 d-flex flex-column gap-1">
																<div className="d-flex justify-content-between align-items-center">
																	<div className="d-flex align-items-center gap-2">
																		<span className={`badge text-bg-${
																			issue.status === 'resolved'
																				? 'success'
																				: issue.status === 'in_review'
																					? 'warning'
																					: 'danger'
																		} text-uppercase`}>
																			{getIssueStatusLabel(issue.status)}
																		</span>
																		<span className="badge text-bg-light border text-uppercase">
																			{copy('Severity', 'Schweregrad', 'Ozbiljnost')}: {getIssueSeverityLabel(issue.severity)}
																		</span>
																	</div>
																	<small className="text-muted">
																		#{issue.id} • {new Date(issue.createdAt).toLocaleDateString()}
																	</small>
																</div>
																<div className="small">
																	{issue.description}
																</div>
																<div className="d-flex justify-content-between align-items-center">
																	<small className="text-muted">
																		{issue.createdByName && <>{copy('Reported by', 'Gemeldet von', 'Prijavio/la')}: {issue.createdByName} • </>}
																		{issue.attachmentFileName && (
																			<span className="text-decoration-underline">
																				Attachment: {issue.attachmentFileName}
																			</span>
																		)}
																	</small>
																</div>
															</li>
														))}
													</ul>
												</div>
											)}

													{/* New Issue Form */}
											{canCreateIssues && isDatabaseTask && (
												<div className="border-top pt-3 mt-3">
													<h5 className="h6 fw-semibold mb-2">{copy('Log a new issue', 'Neues Problem erfassen', 'Prijavi novi problem')}</h5>
													<div className="row g-2 mb-2">
														<div className="col-12 col-md-4">
															<label className="form-label form-label-sm mb-1 small">{copy('Severity', 'Schweregrad', 'Razina ozbiljnosti')}</label>
															<select
																className="form-select form-select-sm"
																value={newIssueSeverity}
																onChange={(e) =>
																	setNewIssueSeverity(e.target.value as typeof newIssueSeverity)
																}
																disabled={isSaving}
															>
																<option value="low">{copy('Low', 'Niedrig', 'Nisko')}</option>
																<option value="medium">{copy('Medium', 'Mittel', 'Srednje')}</option>
																<option value="high">{copy('High', 'Hoch', 'Visoko')}</option>
																<option value="critical">{copy('Critical', 'Kritisch', 'Kritično')}</option>
															</select>
														</div>
														<div className="col-12 col-md-6">
															<label className="form-label form-label-sm mb-1 small">{copy('Link a photo (optional)', 'Foto verknüpfen (optional)', 'Poveži fotografiju (opcionalno)')}</label>
															<select
																className="form-select form-select-sm"
																value={newIssueAttachmentId ?? ''}
																onChange={(e) => {
																	const val = e.target.value;
																	setNewIssueAttachmentId(val ? parseInt(val, 10) : null);
																}}
																disabled={isSaving || attachments.length === 0}
															>
																<option value="">{copy('No linked attachment', 'Kein verknüpfter Anhang', 'Nema povezanog priloga')}</option>
																{attachments.map((att) => (
																	<option key={att.id} value={att.id}>
																		{att.file_name}
																	</option>
																))}
															</select>
														</div>
													</div>
													<div className="d-flex gap-2">
														<textarea
															className="form-control form-control-sm"
															rows={2}
															placeholder={copy('Describe the issue and how it might affect time or cost...', 'Beschreiben Sie das Problem und wie es Zeit oder Kosten beeinflusst...', 'Opišite problem i kako može utjecati na vrijeme ili trošak...')}
															value={newIssueDescription}
															onChange={(e) => setNewIssueDescription(e.target.value)}
															disabled={isSaving}
														/>
														<button
															type="button"
															className="btn btn-sm btn-danger"
															onClick={handleCreateIssue}
															disabled={
																isSaving ||
																!newIssueDescription.trim()
															}
														>
															<i className="bi bi-plus-circle me-1"></i>
															{copy('Add issue', 'Problem hinzufügen', 'Dodaj problem')}
														</button>
													</div>
												</div>
											)}

											{/* Re-plan Section */}
											{canRequestReplan && isDatabaseTask && project && (
												<div className="border-top pt-3 mt-3">
													<div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
														<div>
															<h5 className="h6 fw-semibold mb-1">
																<i className="bi bi-robot me-2"></i>
																{copy('Re-plan after issues', 'Nach Problemen neu planen', 'Ponovno planiraj nakon problema')}
															</h5>
															<p className="text-muted small mb-0">
																{copy(
																	'This will look at the project, its tasks and the logged issues, and suggest a revised timeline and extra cost. It will not change any tasks automatically.',
																	'Dies analysiert das Projekt, seine Aufgaben und die gemeldeten Probleme und schlägt einen überarbeiteten Zeitplan sowie zusätzliche Kosten vor. Aufgaben werden nicht automatisch geändert.',
																	'Ovo će analizirati projekt, njegove zadatke i prijavljene probleme te predložiti revidirani vremenski plan i dodatni trošak. Zadaci se neće automatski mijenjati.'
																)}
															</p>
														</div>
														<div className="d-flex flex-column align-items-stretch gap-2">
															<button
																type="button"
																className="btn btn-sm btn-outline-danger"
																onClick={handleRequestReplan}
																disabled={isRequestingReplan || issues.filter(i => i.status === 'open' || i.status === 'in_review').length === 0}
															>
																{isRequestingReplan ? (
																	<>
																		<span className="spinner-border spinner-border-sm me-2" role="status"></span>
																		{copy('Re-planning...', 'Neuplanung...', 'Ponovno planiranje...')}
																	</>
																) : (
																	<>
																		<i className="bi bi-magic me-2"></i>
																		{copy('Re-plan project after issues', 'Projekt nach Problemen neu planen', 'Ponovno planiraj projekt nakon problema')}
																	</>
																)}
															</button>
														</div>
													</div>

													{replanResult && editableReplan && (
														<div className="mt-3 p-3 border rounded bg-light">
															<h6 className="fw-semibold mb-2">{copy('Re-plan suggestion', 'Neuer Planvorschlag', 'Prijedlog novog plana')}</h6>
															{canApplyReplan ? (
																<textarea
																	className="form-control form-control-sm mb-2"
																	rows={4}
																	value={editableReplan.summary}
																	onChange={(e) =>
																		setEditableReplan(prev =>
																			prev ? { ...prev, summary: e.target.value } : prev
																		)
																	}
																/>
															) : (
																<p className="small mb-2" style={{ whiteSpace: 'pre-wrap' }}>
																	{editableReplan.summary}
																</p>
															)}

															{/* Timeline changes */}
															{editableReplan.task_adjustments && editableReplan.task_adjustments.length > 0 && (
																<div className="mb-3">
																	<h6 className="small text-uppercase text-muted mb-1">
																		{copy('Existing task adjustments', 'Anpassungen an bestehenden Aufgaben', 'Prilagodbe postojećih zadataka')}
																	</h6>
																	<ul className="small mb-0">
																		{editableReplan.task_adjustments.map((adj, idx) => (
																			<li key={idx}>
																				<strong>{adj.task_reference}</strong>{' '}
																				<span className="text-muted">
																					+{adj.add_days}{' '}
																					{copy(adj.add_days === 1 ? 'day' : 'days', adj.add_days === 1 ? 'Tag' : 'Tage', adj.add_days === 1 ? 'dan' : 'dana')}
																				</span>
																				{adj.reason ? (
																					<>
																						<br />
																						<span>{adj.reason}</span>
																					</>
																				) : null}
																			</li>
																		))}
																	</ul>
																</div>
															)}

															{/* Timeline changes */}
															{showTimelineChanges && (
																<div className="mb-3">
																	<h6 className="small text-uppercase text-muted mb-1">{copy('Timeline changes', 'Zeitplanänderungen', 'Promjene vremenskog plana')}</h6>
																	<ul className="small mb-0">
																		{editableReplan.timeline_changes.map((change, idx) => (
																			<li key={idx}>
																				<strong>{change.phase}</strong>{' '}
																				<span className="text-muted">
																					{canApplyReplan ? (
																						<>
																							(+{' '}
																							<input
																								type="number"
																								className="form-control form-control-sm d-inline-block"
																								style={{ width: '4rem' }}
																								value={change.delay_days}
																								onChange={(e) => {
																									const val = parseInt(e.target.value || '0', 10);
																									setEditableReplan(prev => {
																										if (!prev) return prev;
																										const updated = [...prev.timeline_changes];
																										updated[idx] = {
																											...updated[idx],
																											delay_days: isNaN(val) ? 0 : val
																										};
																										return { ...prev, timeline_changes: updated };
																									});
																								}}
																							/>{' '}
																							{copy(change.delay_days === 1 ? 'day' : 'days', change.delay_days === 1 ? 'Tag' : 'Tage', change.delay_days === 1 ? 'dan' : 'dana')})
																						</>
																					) : (
																						<>
																							(+{change.delay_days} {copy(change.delay_days === 1 ? 'day' : 'days', change.delay_days === 1 ? 'Tag' : 'Tage', change.delay_days === 1 ? 'dan' : 'dana')})
																						</>
																					)}
																				</span>
																				<br />
																				<span>{change.description}</span>
																			</li>
																		))}
																	</ul>
																</div>
															)}

															{/* New tasks */}
															{editableReplan.new_tasks?.length > 0 && (
																<div className="mb-3">
																	<h6 className="small text-uppercase text-muted mb-1">{copy('Suggested new tasks', 'Vorgeschlagene neue Aufgaben', 'Predloženi novi zadaci')}</h6>
																	<ul className="small mb-0">
																		{editableReplan.new_tasks.map((nt, idx) => (
																			<li key={idx}>
																				{canApplyReplan ? (
																					<div className="mb-1">
																						<input
																							type="text"
																							className="form-control form-control-sm mb-1 fw-semibold"
																							value={nt.name}
																							onChange={(e) =>
																								setEditableReplan(prev => {
																									if (!prev) return prev;
																									const updated = [...prev.new_tasks];
																									updated[idx] = {
																										...updated[idx],
																										name: e.target.value
																									};
																									return { ...prev, new_tasks: updated };
																								})
																							}
																						/>
																						<div className="d-flex flex-wrap gap-2 align-items-center small text-muted">
																							<span>
																								{copy('Duration', 'Dauer', 'Trajanje')}:{' '}
																								<input
																									type="number"
																									className="form-control form-control-sm d-inline-block"
																									style={{ width: '4rem' }}
																									value={nt.estimated_duration_days}
																									onChange={(e) =>
																										setEditableReplan(prev => {
																											if (!prev) return prev;
																											const updated = [...prev.new_tasks];
																											const val = parseInt(e.target.value || '1', 10);
																											updated[idx] = {
																												...updated[idx],
																												estimated_duration_days: isNaN(val) ? 1 : val
																											};
																											return { ...prev, new_tasks: updated };
																										})
																									}
																								/>{' '}
																								{copy(nt.estimated_duration_days === 1 ? 'day' : 'days', nt.estimated_duration_days === 1 ? 'Tag' : 'Tage', nt.estimated_duration_days === 1 ? 'dan' : 'dana')}
																							</span>
																							<span>
																								{copy('Role', 'Rolle', 'Uloga')}:{' '}
																								<input
																									type="text"
																									className="form-control form-control-sm d-inline-block"
																									style={{ width: '10rem' }}
																									value={nt.suggested_assignee_role}
																									onChange={(e) =>
																										setEditableReplan(prev => {
																											if (!prev) return prev;
																											const updated = [...prev.new_tasks];
																											updated[idx] = {
																												...updated[idx],
																												suggested_assignee_role: e.target.value
																											};
																											return { ...prev, new_tasks: updated };
																										})
																									}
																								/>
																							</span>
																							<span>
																								{copy('Start after', 'Start nach', 'Početak nakon')}:{' '}
																								<input
																									type="text"
																									className="form-control form-control-sm d-inline-block"
																									style={{ width: '12rem' }}
																									value={nt.recommended_start_after_task}
																									onChange={(e) =>
																										setEditableReplan(prev => {
																											if (!prev) return prev;
																											const updated = [...prev.new_tasks];
																											updated[idx] = {
																												...updated[idx],
																												recommended_start_after_task: e.target.value
																											};
																											return { ...prev, new_tasks: updated };
																										})
																									}
																								/>
																							</span>
																						</div>
																					</div>
																				) : (
																					<>
																						<strong>{nt.name}</strong>{' '}
																						<span className="text-muted">
																						({nt.estimated_duration_days} {copy(nt.estimated_duration_days === 1 ? 'day' : 'days', nt.estimated_duration_days === 1 ? 'Tag' : 'Tage', nt.estimated_duration_days === 1 ? 'dan' : 'dana')} • {nt.suggested_assignee_role})
																						</span>
																					</>
																				)}
																				<br />
																				<em className="text-muted">
																					{copy('Start after', 'Start nach', 'Početak nakon')}: {nt.recommended_start_after_task}
																				</em>
																				<br />
																				{canApplyReplan ? (
																					<textarea
																						className="form-control form-control-sm mt-1"
																						rows={3}
																						value={nt.description}
																						onChange={(e) =>
																							setEditableReplan(prev => {
																								if (!prev) return prev;
																								const updated = [...prev.new_tasks];
																								updated[idx] = {
																									...updated[idx],
																									description: e.target.value
																								};
																								return { ...prev, new_tasks: updated };
																							})
																						}
																					/>
																				) : (
																					<span>{nt.description}</span>
																				)}
																			</li>
																		))}
																	</ul>
																</div>
															)}

															{/* Cost impact */}
															{editableReplan.cost_impact && (
																<div>
																	<h6 className="small text-uppercase text-muted mb-1">{copy('Estimated extra cost', 'Geschätzte Zusatzkosten', 'Procijenjeni dodatni trošak')}</h6>
																	{canApplyReplan ? (
																		<div className="d-flex flex-wrap gap-2 align-items-center small mb-2">
																			<span>
																				Min €
																				<input
																					type="number"
																					className="form-control form-control-sm d-inline-block"
																					style={{ width: '6rem' }}
																					value={editableReplan.cost_impact.estimated_extra_cost_min}
																					onChange={(e) =>
																						setEditableReplan(prev => {
																							if (!prev) return prev;
																							const min = parseFloat(e.target.value || '0');
																							const max = Number(prev.cost_impact.estimated_extra_cost_max) || 0;
																							return {
																								...prev,
																								cost_impact: {
																									...prev.cost_impact,
																									estimated_extra_cost_min: min,
																									estimated_extra_cost_mid: Math.round((min + max) / 2)
																								}
																							};
																						})
																					}
																				/>
																			</span>
																			<span>
																				Max €
																				<input
																					type="number"
																					className="form-control form-control-sm d-inline-block"
																					style={{ width: '6rem' }}
																					value={editableReplan.cost_impact.estimated_extra_cost_max}
																					onChange={(e) =>
																						setEditableReplan(prev => {
																							if (!prev) return prev;
																							const min = Number(prev.cost_impact.estimated_extra_cost_min) || 0;
																							const max = parseFloat(e.target.value || '0');
																							return {
																								...prev,
																								cost_impact: {
																									...prev.cost_impact,
																									estimated_extra_cost_max: max,
																									estimated_extra_cost_mid: Math.round((min + max) / 2)
																								}
																							};
																						})
																					}
																				/>
																			</span>
																		</div>
																	) : (
																		<p className="small mb-1">
																			<b>
																				€
																				{Math.round(
																					editableReplan.cost_impact.estimated_extra_cost_min
																				)}{' '}
																				– €
																				{Math.round(
																					editableReplan.cost_impact.estimated_extra_cost_max
																				)}
																			</b>
																		</p>
																	)}
																	{(editableReplan.cost_impact.extra_materials?.length > 0 ||
																		editableReplan.cost_impact.extra_labor_hours?.length > 0) && (
																		<div className="small text-muted">
																			{editableReplan.cost_impact.extra_materials?.length > 0 && (
																				<>
																					<div className="fw-semibold mt-1">{copy('Materials', 'Materialien', 'Materijali')}</div>
																					<ul className="mb-1">
																						{editableReplan.cost_impact.extra_materials.map((m, idx) => (
																							<li key={idx}>
																								{canApplyReplan ? (
																									<>
																										<input
																											type="text"
																											className="form-control form-control-sm d-inline-block me-1"
																											style={{ width: '12rem' }}
																											value={m.name}
																											onChange={(e) =>
																												setEditableReplan(prev => {
																													if (!prev) return prev;
																													const extra = [...prev.cost_impact.extra_materials];
																													extra[idx] = { ...extra[idx], name: e.target.value };
																													return {
																														...prev,
																														cost_impact: { ...prev.cost_impact, extra_materials: extra }
																													};
																												})
																											}
																										/>
																										<input
																											type="number"
																											className="form-control form-control-sm d-inline-block me-1"
																											style={{ width: '5rem' }}
																											value={m.quantity}
																											onChange={(e) =>
																												setEditableReplan(prev => {
																													if (!prev) return prev;
																													const extra = [...prev.cost_impact.extra_materials];
																													extra[idx] = {
																														...extra[idx],
																														quantity: parseFloat(e.target.value || '0')
																													};
																													return {
																														...prev,
																														cost_impact: { ...prev.cost_impact, extra_materials: extra }
																													};
																												})
																											}
																										/>
																										<input
																											type="text"
																											className="form-control form-control-sm d-inline-block me-1"
																											style={{ width: '4rem' }}
																											value={m.unit}
																											onChange={(e) =>
																												setEditableReplan(prev => {
																													if (!prev) return prev;
																													const extra = [...prev.cost_impact.extra_materials];
																													extra[idx] = { ...extra[idx], unit: e.target.value };
																													return {
																														...prev,
																														cost_impact: { ...prev.cost_impact, extra_materials: extra }
																													};
																												})
																											}
																										/>
																										€
																										<input
																											type="number"
																											className="form-control form-control-sm d-inline-block ms-1"
																											style={{ width: '6rem' }}
																											value={m.cost}
																											onChange={(e) =>
																												setEditableReplan(prev => {
																													if (!prev) return prev;
																													const extra = [...prev.cost_impact.extra_materials];
																													extra[idx] = {
																														...extra[idx],
																														cost: parseFloat(e.target.value || '0')
																													};
																													return {
																														...prev,
																														cost_impact: { ...prev.cost_impact, extra_materials: extra }
																													};
																												})
																											}
																										/>
																									</>
																								) : (
																									<>
																										{m.name}: {m.quantity} {m.unit} (≈ €
																										{Math.round(m.cost)})
																									</>
																								)}
																							</li>
																						))}
																					</ul>
																				</>
																			)}
																			{editableReplan.cost_impact.extra_labor_hours?.length > 0 && (
																				<>
																					<div className="fw-semibold mt-1">{copy('Labour', 'Arbeitsleistung', 'Rad')}</div>
																					<ul className="mb-0">
																						{editableReplan.cost_impact.extra_labor_hours.map((l, idx) => (
																							<li key={idx}>
																								{canApplyReplan ? (
																									<>
																										<input
																											type="text"
																											className="form-control form-control-sm d-inline-block me-1"
																											style={{ width: '10rem' }}
																											value={l.role}
																											onChange={(e) =>
																												setEditableReplan(prev => {
																													if (!prev) return prev;
																													const extra = [...prev.cost_impact.extra_labor_hours];
																													extra[idx] = { ...extra[idx], role: e.target.value };
																													return {
																														...prev,
																														cost_impact: { ...prev.cost_impact, extra_labor_hours: extra }
																													};
																												})
																											}
																										/>
																										<input
																											type="number"
																											className="form-control form-control-sm d-inline-block me-1"
																											style={{ width: '5rem' }}
																											value={l.hours}
																											onChange={(e) =>
																												setEditableReplan(prev => {
																													if (!prev) return prev;
																													const extra = [...prev.cost_impact.extra_labor_hours];
																													const hours = parseFloat(e.target.value || '0');
																													const rate = extra[idx].ratePerHour;
																													extra[idx] = {
																														...extra[idx],
																														hours,
																														total: hours * rate
																													};
																													return {
																														...prev,
																														cost_impact: { ...prev.cost_impact, extra_labor_hours: extra }
																													};
																												})
																											}
																										/>
																										h × €
																										<input
																											type="number"
																											className="form-control form-control-sm d-inline-block me-1"
																											style={{ width: '5rem' }}
																											value={l.ratePerHour}
																											onChange={(e) =>
																												setEditableReplan(prev => {
																													if (!prev) return prev;
																													const extra = [...prev.cost_impact.extra_labor_hours];
																													const rate = parseFloat(e.target.value || '0');
																													const hours = extra[idx].hours;
																													extra[idx] = {
																														...extra[idx],
																														ratePerHour: rate,
																														total: hours * rate
																													};
																													return {
																														...prev,
																														cost_impact: { ...prev.cost_impact, extra_labor_hours: extra }
																													};
																												})
																											}
																										/>
																										(≈ €
																										{Math.round(l.total)})
																									</>
																								) : (
																									<>
																										{l.role}: {l.hours} h × €
																										{Math.round(l.ratePerHour)}/h (≈ €
																										{Math.round(l.total)})
																									</>
																								)}
																							</li>
																						))}
																					</ul>
																				</>
																			)}
																		</div>
																	)}
																	{canApplyReplan && (
																		<div className="mt-3 d-flex justify-content-end">
																			<button
																				type="button"
																				className="btn btn-sm btn-success"
																				onClick={async () => {
																					if (!replanResult || !editableReplan || !user || !canApplyReplan) return;
																					const userIdNum =
																						typeof user.id === 'string'
																							? parseInt(user.id, 10)
																							: user.id;
																					if (!userIdNum) return;
																					try {
																						const response = await applyProjectReplan(replanResult.replan_id, {
																							user_id: userIdNum,
																							replan: editableReplan
																						});
																						alert(
																							`Changes applied.\nNew budget: €${Math.round(
																								response.new_budget
																							)}${response.new_due_date ? `\nNew due date: ${response.new_due_date}` : ''}`
																						);
																						onUpdate();
																					} catch (err) {
																						console.error('Failed to apply replan', err);
																						alert('Failed to apply changes. Please try again.');
																					}
																				}}
																			>
																				{copy('Apply changes to project', 'Änderungen auf Projekt anwenden', 'Primijeni promjene na projekt')}
																			</button>
																		</div>
																	)}
																</div>
															)}
														</div>
													)}
												</div>
											)}
										</div>
									</div>

									{/* File Attachments Section */}
									<div className={`${sectionCardClass} mb-4`}>
										<div className="card-body p-4">
											<h4 className="h6 fw-bold text-success-emphasis mb-3">
												<i className="bi bi-paperclip me-2"></i>{copy('Attachments', 'Anhänge', 'Prilozi')}
											</h4>
											
											{/* Upload Section */}
											{isDatabaseTask && canAddNotesAndAttachments && (
												<div className="mb-3 p-3 bg-light rounded">
													<input
														ref={fileInputRef}
														type="file"
														className="d-none"
														onChange={handleFileUpload}
														accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
													/>
													<input
														ref={cameraInputRef}
														type="file"
														className="d-none"
														onChange={handleFileUpload}
												accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif"
														capture="environment"
													/>
													<div className="mb-2">
														<input
															type="text"
															className="form-control form-control-sm"
															placeholder={copy('Optional description...', 'Optionale Beschreibung...', 'Opcionalni opis...')}
															value={uploadDescription}
															onChange={(e) => setUploadDescription(e.target.value)}
															disabled={isUploading}
														/>
													</div>
													<button
														type="button"
														className="btn btn-sm btn-outline-success w-100"
														onClick={handleFileSelect}
														disabled={isUploading || isSaving}
													>
														{isUploading ? (
															<>
																<span className="spinner-border spinner-border-sm me-2" role="status"></span>
																{copy('Uploading...', 'Lädt hoch...', 'Učitavanje...')}
															</>
														) : (
															<>
																<i className="bi bi-upload me-2"></i>{copy('Upload File', 'Datei hochladen', 'Prenesi datoteku')}
															</>
														)}
													</button>
													<button
														type="button"
														className="btn btn-sm btn-outline-success w-100 mt-2 d-lg-none"
														onClick={() => cameraInputRef.current?.click()}
														disabled={isUploading || isSaving}
													>
														<i className="bi bi-camera me-2" aria-hidden />
														{copy('Take photo', 'Foto aufnehmen', 'Snimi fotografiju')}
													</button>
													<small className="text-muted d-block mt-2">
														{copy('Max 10MB. Allowed: images, PDF, documents, text files, archives', 'Max 10MB. Erlaubt: Bilder, PDF, Dokumente, Textdateien, Archive', 'Maks 10MB. Dopušteno: slike, PDF, dokumenti, tekstualne datoteke, arhive')}
													</small>
												</div>
											)}

											{/* Attachments List */}
											{attachments.length > 0 ? (
												<>
													{/* Image Attachments Grid */}
													{attachments.some(a => {
														const mime = a.mime_type?.toLowerCase() || '';
														const fileName = a.file_name?.toLowerCase() || '';
														return mime.startsWith('image/') || 
															fileName.endsWith('.jpg') || 
															fileName.endsWith('.jpeg') || 
															fileName.endsWith('.png') || 
															fileName.endsWith('.gif') || 
															fileName.endsWith('.webp');
													}) && (
														<div className="mb-3">
															<h5 className="h6 text-muted mb-2">{copy('Images', 'Bilder', 'Slike')}</h5>
															<div className="row g-2">
																{attachments
																	.filter(a => {
																		const mime = a.mime_type?.toLowerCase() || '';
																		const fileName = a.file_name?.toLowerCase() || '';
																		return mime.startsWith('image/') || 
																			fileName.endsWith('.jpg') || 
																			fileName.endsWith('.jpeg') || 
																			fileName.endsWith('.png') || 
																			fileName.endsWith('.gif') || 
																			fileName.endsWith('.webp');
																	})
																	.map((attachment) => (
																		<div key={attachment.id} className="col-6 col-md-4 col-lg-3">
																			<div className="card border position-relative">
																				<a
																					href={getAttachmentFileUrl(attachment.id, true)}
																					download={attachment.file_name}
																					target="_blank"
																					rel="noopener noreferrer"
																					className="text-decoration-none d-block"
																				>
																					<div style={{ 
																						height: '150px', 
																						backgroundColor: '#f8f9fa',
																						display: 'flex',
																						alignItems: 'center',
																						justifyContent: 'center',
																						overflow: 'hidden',
																						position: 'relative'
																					}}>
																						<img
																							src={getAttachmentFileUrl(attachment.id)}
																							alt={attachment.file_name}
																							style={{
																								width: '100%',
																								height: '100%',
																								objectFit: 'cover',
																								cursor: 'pointer'
																							}}
																							onError={(e) => {
																								// Show fallback icon if image fails to load
																								const img = e.target as HTMLImageElement;
																								img.style.display = 'none';
																								const parent = img.parentElement;
																								if (parent && !parent.querySelector('.image-error-fallback')) {
																									const fallback = document.createElement('div');
																									fallback.className = 'image-error-fallback';
																									fallback.innerHTML = '<i class="bi bi-image text-muted" style="font-size: 3rem;"></i>';
																									fallback.style.cssText = 'display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;';
																									parent.appendChild(fallback);
																								}
																							}}
																						/>
																					</div>
																				</a>
																				<div className="card-body p-2">
																					<div className="d-flex justify-content-between align-items-start gap-2">
																						<div className="flex-grow-1" style={{ minWidth: 0 }}>
																							<div className="text-truncate small fw-semibold" title={attachment.file_name}>
																								{attachment.file_name}
																							</div>
																							{attachment.description && (
																								<div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }} title={attachment.description}>
																									{attachment.description}
																								</div>
																							)}
																						</div>
																						{isDatabaseTask && canAddNotesAndAttachments && (
																							<button
																								type="button"
																								className="btn btn-sm btn-outline-danger p-1"
																								onClick={() => handleDeleteAttachment(attachment.id)}
																								disabled={isSaving}
																								title="Delete file"
																								style={{ flexShrink: 0 }}
																							>
																								<i className="bi bi-trash" style={{ fontSize: '0.75rem' }}></i>
																							</button>
																						)}
																					</div>
																				</div>
																			</div>
																		</div>
																	))}
															</div>
														</div>
													)}

													{/* Other File Attachments List */}
													{attachments.some(a => !a.mime_type?.startsWith('image/')) && (
														<div>
															{attachments.some(a => a.mime_type?.startsWith('image/')) && (
																<h5 className="h6 text-muted mb-2">{copy('Other Files', 'Andere Dateien', 'Druge datoteke')}</h5>
															)}
															<div className="list-group">
																{attachments
																	.filter(a => !a.mime_type?.startsWith('image/'))
																	.map((attachment) => (
																		<div key={attachment.id} className="list-group-item d-flex justify-content-between align-items-center">
																			<div className="d-flex align-items-center gap-2 flex-grow-1">
																				<i className={`bi ${getFileIcon(attachment.mime_type)} text-success fs-5`}></i>
																				<div className="flex-grow-1">
																					<a
																						href={getAttachmentFileUrl(attachment.id, true)}
																						download={attachment.file_name}
																						target="_blank"
																						rel="noopener noreferrer"
																						className="text-decoration-none fw-semibold"
																					>
																						{attachment.file_name}
																					</a>
																					{attachment.description && (
																						<div className="text-muted small">{attachment.description}</div>
																					)}
																					<div className="text-muted small">
																						{attachment.file_size_formatted || copy('Unknown size', 'Unbekannte Größe', 'Nepoznata veličina')}
																						{attachment.uploaded_by_name && ` • ${copy('Uploaded by', 'Hochgeladen von', 'Prenio/la')}: ${attachment.uploaded_by_name}`}
																						{` • ${new Date(attachment.created_at).toLocaleDateString()}`}
																					</div>
																				</div>
																			</div>
																			{isDatabaseTask && canAddNotesAndAttachments && (
																				<button
																					type="button"
																					className="btn btn-sm btn-outline-danger"
																					onClick={() => handleDeleteAttachment(attachment.id)}
																					disabled={isSaving}
																					title="Delete file"
																				>
																					<i className="bi bi-trash"></i>
																				</button>
																			)}
																		</div>
																	))}
															</div>
														</div>
													)}
												</>
											) : (
												<p className="text-muted small mb-0">
													<em>{copy('No attachments yet. Upload files to keep documents, photos, or other files related to this task.', 'Noch keine Anhänge. Laden Sie Dateien hoch, um Dokumente, Fotos und andere Dateien zu dieser Aufgabe zu speichern.', 'Još nema priloga. Prenesite datoteke za spremanje dokumenata, fotografija i drugih datoteka vezanih uz ovaj zadatak.')}</em>
												</p>
											)}
										</div>
									</div>
								</div>

								<div className="col-12 col-lg-4">
									{/* Project Info */}
									{project && (
										<div className={`${sectionCardClass} mb-4`}>
											<div className="card-body p-4">
												<h4 className="h6 fw-bold text-success-emphasis mb-3">{copy('Project', 'Projekt', 'Projekt')}</h4>
												<div className="mb-2">
													<div className="text-muted small">{copy('Project', 'Projekt', 'Projekt')}</div>
													{projectDetailPath ? (
														<Link
															to={projectDetailPath}
															className="fw-semibold text-decoration-none text-success"
															title={copy('Open project', 'Projekt öffnen', 'Otvori projekt')}
														>
															{[project.client, project.name].filter(Boolean).join(' – ') || copy('Untitled project', 'Unbenanntes Projekt', 'Projekt bez naslova')}
														</Link>
													) : (
														<div className="fw-semibold">
															{[project.client, project.name].filter(Boolean).join(' – ') || copy('Untitled project', 'Unbenanntes Projekt', 'Projekt bez naslova')}
														</div>
													)}
												</div>
											</div>
										</div>
									)}

									{/* Assigned Gardeners */}
									<div className={`${sectionCardClass} mb-4`}>
										<div className="card-body p-4">
											<h4 className="h6 fw-bold text-success-emphasis mb-3">
												<i className="bi bi-people me-2"></i>{copy('Assigned Gardeners', 'Zugewiesene Gärtner', 'Dodijeljeni vrtlari')}
											</h4>
											{isEditing ? (
												<div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
													{gardeners.length > 0 ? (
														gardeners.map((gardener) => (
															<div key={gardener.id} className="form-check">
																<input
																	className="form-check-input"
																	type="checkbox"
																	id={`modal-gardener-${gardener.id}`}
																	checked={taskData.selectedGardeners.includes(String(gardener.id))}
																	onChange={() => {
																		const gardenerId = String(gardener.id);
																		setTaskData(prev => prev ? {
																			...prev,
																			selectedGardeners: prev.selectedGardeners.includes(gardenerId)
																				? prev.selectedGardeners.filter(id => id !== gardenerId)
																				: [...prev.selectedGardeners, gardenerId]
																		} : null);
																	}}
																	disabled={isSaving}
																/>
																<label className="form-check-label" htmlFor={`modal-gardener-${gardener.id}`}>
																	{gardener.name} {gardener.role && `(${gardener.role})`}
																</label>
															</div>
														))
													) : (
														<p className="text-muted small mb-0">{copy('No gardeners available', 'Keine Gärtner verfügbar', 'Nema dostupnih vrtlara')}</p>
													)}
												</div>
											) : (
												<>
													{assignedGardeners.length > 0 ? (
														<div className="d-flex flex-column gap-2">
															{assignedGardeners.map((gardener) => (
																<div key={gardener.id} className="d-flex align-items-center gap-2">
																	<i className="bi bi-person-circle text-success"></i>
																	<span>{gardener.name}</span>
																</div>
															))}
														</div>
													) : (
														<p className="text-muted small mb-0">{copy('No gardeners assigned', 'Keine Gärtner zugewiesen', 'Nema dodijeljenih vrtlara')}</p>
													)}
												</>
											)}
										</div>
									</div>

									{/* Task Type & Critical Path */}
									<div className={sectionCardClass}>
										<div className="card-body p-4">
											<h4 className="h6 fw-bold text-success-emphasis mb-3">{copy('Task Info', 'Aufgabeninfo', 'Informacije o zadatku')}</h4>
											<div className="mb-2">
												<div className="text-muted small">{copy('Type', 'Typ', 'Tip')}</div>
												<div className="fw-semibold text-capitalize">{task.type || copy('Task', 'Aufgabe', 'Zadatak')}</div>
											</div>
											{task.isCritical && (
												<div className="mt-3">
													<span className="badge text-bg-danger">
														<i className="bi bi-exclamation-triangle me-1"></i>{copy('Critical Path', 'Kritischer Pfad', 'Kritični put')}
													</span>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer bg-light border-top px-4 py-3">
							{isEditing ? (
								<>
									<button
										type="button"
										className="btn btn-secondary"
										onClick={() => {
											setIsEditing(false);
											if (task) {
												setTaskData(taskToFormState(task));
											}
										}}
										disabled={isSaving}
									>
										{copy('Cancel', 'Abbrechen', 'Odustani')}
									</button>
									<button
										type="button"
										className="btn btn-success"
										onClick={handleSave}
										disabled={isSaving}
									>
										{isSaving ? (
											<>
												<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
												{copy('Saving...', 'Speichern...', 'Spremanje...')}
											</>
										) : (
											copy('Save Changes', 'Änderungen speichern', 'Spremi promjene')
										)}
									</button>
								</>
							) : (
								<>
									<div className="d-flex gap-2">
										{canUseEditMode && isDatabaseTask && taskId && (
											<button
												type="button"
												className="btn btn-outline-danger"
												onClick={handleDelete}
												disabled={isDeleting}
											>
												{isDeleting ? copy('Deleting...', 'Löschen...', 'Brisanje...') : <><i className="bi bi-trash me-2"></i>{copy('Delete', 'Löschen', 'Izbriši')}</>}
											</button>
										)}
										<div className="ms-auto d-flex gap-2">
											{hasInlineUnsavedChanges && (
												<button
													type="button"
													className="btn btn-success"
													onClick={saveInlineProgressAndStatus}
													disabled={isSaving}
												>
													{isSaving ? (
														<>
															<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
															{copy('Saving...', 'Speichern...', 'Spremanje...')}
														</>
													) : (
														copy('Save Changes', 'Änderungen speichern', 'Spremi promjene')
													)}
												</button>
											)}
											<button
												type="button"
												className="btn btn-secondary"
												onClick={handleAttemptClose}
											>
												{copy('Close', 'Schließen', 'Zatvori')}
											</button>
											{canUseEditMode && (
												<button
													type="button"
													className="btn btn-success"
													onClick={() => setIsEditing(true)}
												>
													<i className="bi bi-pencil me-2"></i>{copy('Edit Task', 'Aufgabe bearbeiten', 'Uredi zadatak')}
												</button>
											)}
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
			<div
				className="modal-backdrop fade show"
				onClick={() => {
					if (!isEditing) {
						handleAttemptClose();
					}
				}}
				style={{ opacity: 0.25, zIndex: 19900 }}
			></div>
		</>
	);
}

